import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    CreateWidget,
    CreateWidgetFailure,
    CreateWidgetSuccess,
    DeleteWidget,
    DeleteWidgetFailure,
    DeleteWidgetSuccess,
    FetchWidget,
    FetchWidgetFailure,
    FetchWidgets,
    FetchWidgetsFailure,
    FetchWidgetsSuccess,
    FetchWidgetSuccess,
    SetWidget,
    SetWidgets,
    UnsetWidget,
    UpdateWidget,
    UpdateWidgetFailure,
    UpdateWidgetSuccess,
} from '../actions';
import { AddWidgets } from '../actions/add-entities.action';
import { WidgetApiService } from '../services/widget.api-service';
import { MaxBrainWidget } from './widget.model';
import { WidgetStateModel } from './widget.state-model';

@State<WidgetStateModel>({
    name: 'widget',
    defaults: new WidgetStateModel(),
})
@Injectable()
export class WidgetState extends EntityState<MaxBrainWidget> implements IEntityState<MaxBrainWidget> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: WidgetStateModel, id: string = state.item): MaxBrainWidget {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: WidgetStateModel): MaxBrainWidget[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(WidgetState.sortProperties));

        return toReturn;
    }

    constructor(widgetService: WidgetApiService) {
        super(widgetService, 0);

        this._propstoSearch = MaxBrainWidget.props_to_search;
    }

    @Action(AddWidgets)
    addEntities(ctx: StateContext<WidgetStateModel>, action: AddWidgets): void {
        this._addEntities(ctx, action);
    }

    @Action(SetWidget)
    setEntity(ctx: StateContext<WidgetStateModel>, action: SetWidget): void {
        this._setEntity(ctx, action, AddWidgets);
    }

    @Action(UnsetWidget)
    unsetEntity(ctx: StateContext<WidgetStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateWidget)
    createEntity(ctx: StateContext<WidgetStateModel>, action: CreateWidget): void {
        this._createEntity(ctx, action, CreateWidgetSuccess, CreateWidgetFailure, AddWidgets).then(() => {});
    }

    @Action(UpdateWidget)
    updateEntity(ctx: StateContext<WidgetStateModel>, action: UpdateWidget): void {
        this._updateEntity(ctx, action, UpdateWidgetSuccess, UpdateWidgetFailure, AddWidgets).then(() => {});
    }

    @Action(DeleteWidget)
    deleteEntity(ctx: StateContext<WidgetStateModel>, action: DeleteWidget): void {
        this._deleteEntity(ctx, action, DeleteWidgetSuccess, DeleteWidgetFailure).then(() => {});
    }

    @Action(FetchWidget)
    fetchEntity(ctx: StateContext<WidgetStateModel>, action: FetchWidget): void {
        this._fetchEntity(ctx, action, FetchWidgetSuccess, FetchWidgetFailure, AddWidgets, SetWidget).then(() => {});
    }

    @Action(FetchWidgetSuccess)
    fetchEntitySuccess(ctx: StateContext<WidgetStateModel>, action: FetchWidgetSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchWidgetFailure)
    fetchEntityFailure(ctx: StateContext<WidgetStateModel>, action: FetchWidgetFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetWidgets)
    setEntities(ctx: StateContext<WidgetStateModel>, action: SetWidgets): void {
        this._setEntities(ctx, action);
    }

    @Action(FetchWidgets)
    fetchEntities(ctx: StateContext<WidgetStateModel>): void {
        this._fetchEntities(ctx, FetchWidgetsSuccess, FetchWidgetsFailure, SetWidgets).then(() => {});
    }

    @Action(FetchWidgetsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<WidgetStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(FetchWidgetsFailure)
    fetchEntitiesFailure(ctx: StateContext<WidgetStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }
}
